"use strict";
import { BigNumber } from "@ethersproject/bignumber";
import { MixedRouteSDK } from "@uniswap/router-sdk";
import { CurrencyAmount, Token } from "@uniswap/sdk-core";
import { Pair, Route as V2Route } from "@uniswap/v2-sdk";
import { Pool as V3Pool, Route as V3Route } from "@uniswap/v3-sdk";
import { Pool as V4Pool, Route as V4Route } from "@uniswap/v4-sdk";
import { useMemo } from "react";
import {
  AutoSlippage,
  HooksOptions,
  ProtocolItems,
  Routing,
  RoutingPreference,
  ChainId as TradingApiChainId,
  Urgency
} from "data/tradingApi/__generated__/index";
import { getChainInfo } from "features/chains/chainInfo";
import { isL2ChainId } from "features/chains/utils";
import { DynamicConfigs, SwapConfigKey } from "features/gating/configs";
import { FeatureFlags } from "features/gating/flags";
import { useDynamicConfigValue, useFeatureFlag } from "features/gating/hooks";
import { NativeCurrency } from "features/tokens/NativeCurrency";
import { ValueType, getCurrencyAmount } from "features/tokens/getCurrencyAmount";
import {
  BridgeTrade,
  ClassicTrade,
  PriorityOrderTrade,
  UniswapXV2Trade,
  UniswapXV3Trade
} from "features/transactions/swap/types/trade";
import {
  DEFAULT_PROTOCOL_OPTIONS,
  useProtocolsForChain
} from "features/transactions/swap/utils/protocols";
import { areAddressesEqual } from "utils/addresses";
import { currencyId } from "utils/uniswapCurrencyId";
import { logger } from "utilities/src/logger/logger";
import { isInterface } from "utilities/src/platform";
export const NATIVE_ADDRESS_FOR_TRADING_API = "0x0000000000000000000000000000000000000000";
export const SWAP_GAS_URGENCY_OVERRIDE = isInterface ? Urgency.NORMAL : void 0;
export function transformTradingApiResponseToTrade(params) {
  const { currencyIn, currencyOut, tradeType, deadline, data } = params;
  switch (data?.routing) {
    case Routing.CLASSIC: {
      const routes = computeRoutes(currencyIn.isNative, currencyOut.isNative, data);
      if (!routes || !deadline) {
        return null;
      }
      return new ClassicTrade({
        quote: data,
        deadline,
        v2Routes: routes?.flatMap((r) => r?.routev2 ? { ...r, routev2: r.routev2 } : []) ?? [],
        v3Routes: routes?.flatMap((r) => r?.routev3 ? { ...r, routev3: r.routev3 } : []) ?? [],
        v4Routes: routes?.flatMap((r) => r?.routev4 ? { ...r, routev4: r.routev4 } : []) ?? [],
        mixedRoutes: routes?.flatMap((r) => r?.mixedRoute ? { ...r, mixedRoute: r.mixedRoute } : []) ?? [],
        tradeType
      });
    }
    case Routing.PRIORITY:
    case Routing.DUTCH_V3:
    case Routing.DUTCH_V2: {
      const { quote } = data;
      if (!areAddressesEqual(currencyIn.wrapped.address, quote.orderInfo.input.token) || // UniswapX quotes should use wrapped native as input, rather than the native token
      !areAddressesEqual(getTokenAddressForApi(currencyOut), quote.orderInfo.outputs[0]?.token)) {
        return null;
      }
      const isPriority = data.routing === Routing.PRIORITY;
      if (isPriority) {
        return new PriorityOrderTrade({ quote: data, currencyIn, currencyOut, tradeType });
      } else if (data.routing === Routing.DUTCH_V2) {
        return new UniswapXV2Trade({ quote: data, currencyIn, currencyOut, tradeType });
      } else {
        return new UniswapXV3Trade({ quote: data, currencyIn, currencyOut, tradeType });
      }
    }
    case Routing.BRIDGE: {
      return new BridgeTrade({ quote: data, currencyIn, currencyOut, tradeType });
    }
    default: {
      return null;
    }
  }
}
export function computeRoutes(tokenInIsNative, tokenOutIsNative, quoteResponse) {
  if (!quoteResponse || !quoteResponse.quote || !isClassicQuote(quoteResponse.quote)) {
    return void 0;
  }
  const { quote } = quoteResponse;
  if (!quote.route || quote.route?.length === 0) {
    return void 0;
  }
  const tokenIn = quote.route[0]?.[0]?.tokenIn;
  const tokenOut = quote.route[0]?.[quote.route[0]?.length - 1]?.tokenOut;
  if (!tokenIn || !tokenOut) {
    throw new Error("Expected both tokenIn and tokenOut to be present");
  }
  if (!tokenIn.chainId || tokenOut.chainId === void 0 || !tokenIn.address || !tokenOut.address || !tokenIn.decimals || !tokenOut.decimals) {
    throw new Error("Expected all token properties to be present");
  }
  const parsedCurrencyIn = tokenInIsNative ? NativeCurrency.onChain(tokenIn.chainId) : parseTokenApi(tokenIn);
  const parsedCurrencyOut = tokenOutIsNative ? NativeCurrency.onChain(tokenOut.chainId) : parseTokenApi(tokenOut);
  try {
    return quote.route.map((route) => {
      if (route.length === 0) {
        throw new Error("Expected route to have at least one pair or pool");
      }
      const inputAmount = getCurrencyAmount({
        value: route[0]?.amountIn,
        valueType: ValueType.Raw,
        currency: parsedCurrencyIn
      });
      const outputAmount = getCurrencyAmount({
        value: route[route.length - 1]?.amountOut,
        valueType: ValueType.Raw,
        currency: parsedCurrencyOut
      });
      if (!inputAmount || !outputAmount) {
        throw new Error("Expected both amountIn and amountOut to be present");
      }
      const isOnlyV2 = isV2OnlyRouteApi(route);
      const isOnlyV3 = isV3OnlyRouteApi(route);
      const isOnlyV4 = isV4OnlyRouteApi(route);
      const v4Routes = route.filter((r) => r.type === "v4-pool");
      return {
        routev4: isOnlyV4 ? new V4Route(v4Routes.map(parseV4PoolApi), parsedCurrencyIn, parsedCurrencyOut) : null,
        routev3: isOnlyV3 ? new V3Route(route.map(parseV3PoolApi), parsedCurrencyIn, parsedCurrencyOut) : null,
        routev2: isOnlyV2 ? new V2Route(route.map(parseV2PairApi), parsedCurrencyIn, parsedCurrencyOut) : null,
        mixedRoute: !isOnlyV3 && !isOnlyV2 && !isOnlyV4 ? new MixedRouteSDK(route.map(parseMixedRouteApi), parsedCurrencyIn, parsedCurrencyOut) : null,
        inputAmount,
        outputAmount
      };
    });
  } catch (e) {
    logger.error(e, {
      tags: { file: "tradingApi.ts", function: "computeRoutes" },
      extra: {
        input: parsedCurrencyIn.address,
        output: parsedCurrencyOut.address,
        inputChainId: parsedCurrencyIn.chainId,
        outputChainId: parsedCurrencyOut.chainId
      }
    });
    return void 0;
  }
}
function parseTokenApi(token) {
  const { address, chainId, decimals, symbol, buyFeeBps, sellFeeBps } = token;
  if (!chainId || !address || !decimals || !symbol) {
    throw new Error("Expected token to have chainId, address, decimals, and symbol");
  }
  if (address === NATIVE_ADDRESS_FOR_TRADING_API) {
    throw new Error("Cannot parse native currency as an erc20 Token");
  }
  return new Token(
    chainId,
    address,
    parseInt(decimals.toString(), 10),
    symbol,
    /**name=*/
    void 0,
    false,
    buyFeeBps ? BigNumber.from(buyFeeBps) : void 0,
    sellFeeBps ? BigNumber.from(sellFeeBps) : void 0
  );
}
function parseV4PoolApi({
  fee,
  sqrtRatioX96,
  liquidity,
  tickCurrent,
  tickSpacing,
  hooks,
  tokenIn,
  tokenOut
}) {
  if (!tokenIn.address || !tokenOut.address || !tokenIn.chainId || !tokenOut.chainId) {
    throw new Error("Expected V4 route to have defined addresses and chainIds");
  }
  const inputIsNative = tokenIn.address === NATIVE_ADDRESS_FOR_TRADING_API;
  const outputIsNative = tokenOut.address === NATIVE_ADDRESS_FOR_TRADING_API;
  const currencyIn = inputIsNative ? NativeCurrency.onChain(tokenIn.chainId) : parseTokenApi(tokenIn);
  const currencyOut = outputIsNative ? NativeCurrency.onChain(tokenOut.chainId) : parseTokenApi(tokenOut);
  return new V4Pool(
    currencyIn,
    currencyOut,
    Number(fee),
    Number(tickSpacing),
    hooks,
    sqrtRatioX96,
    liquidity,
    Number(tickCurrent)
  );
}
function parseV3PoolApi({
  fee,
  sqrtRatioX96,
  liquidity,
  tickCurrent,
  tokenIn,
  tokenOut
}) {
  if (!tokenIn || !tokenOut || !fee || !sqrtRatioX96 || !liquidity || !tickCurrent) {
    throw new Error("Expected pool values to be present");
  }
  return new V3Pool(
    parseTokenApi(tokenIn),
    parseTokenApi(tokenOut),
    parseInt(fee, 10),
    sqrtRatioX96,
    liquidity,
    parseInt(tickCurrent, 10)
  );
}
function parseV2PairApi({ reserve0, reserve1 }) {
  if (!reserve0?.token || !reserve1?.token || !reserve0.quotient || !reserve1.quotient) {
    throw new Error("Expected pool values to be present");
  }
  return new Pair(
    CurrencyAmount.fromRawAmount(parseTokenApi(reserve0.token), reserve0.quotient),
    CurrencyAmount.fromRawAmount(parseTokenApi(reserve1.token), reserve1.quotient)
  );
}
function parseMixedRouteApi(pool) {
  if (isV2Pool(pool)) {
    return parseV2PairApi(pool);
  } else if (isV3Pool(pool)) {
    return parseV3PoolApi(pool);
  } else if (isV4Pool(pool)) {
    return parseV4PoolApi(pool);
  }
  throw new Error("Invalid pool type");
}
function isV2Pool(pool) {
  return pool.type === "v2-pool";
}
function isV3Pool(pool) {
  return pool.type === "v3-pool";
}
function isV4Pool(pool) {
  return pool.type === "v4-pool";
}
function isV2OnlyRouteApi(route) {
  return route.every(isV2Pool);
}
function isV3OnlyRouteApi(route) {
  return route.every(isV3Pool);
}
function isV4OnlyRouteApi(route) {
  return route.every(isV4Pool);
}
export function getTokenAddressFromChainForTradingApi(address, chainId) {
  if (address === getChainInfo(chainId).nativeCurrency.address) {
    return NATIVE_ADDRESS_FOR_TRADING_API;
  }
  return address;
}
export function getTokenAddressForApi(currency) {
  if (!currency) {
    return void 0;
  }
  return currency.isNative ? NATIVE_ADDRESS_FOR_TRADING_API : currency.address;
}
const SUPPORTED_TRADING_API_CHAIN_IDS = Object.values(TradingApiChainId).filter(
  (value) => typeof value === "number"
);
function isTradingApiSupportedChainId(chainId) {
  if (!chainId) {
    return false;
  }
  return Object.values(SUPPORTED_TRADING_API_CHAIN_IDS).includes(chainId);
}
export function toTradingApiSupportedChainId(chainId) {
  if (!chainId || !isTradingApiSupportedChainId(chainId)) {
    return void 0;
  }
  return chainId;
}
export function isClassicQuote(quote) {
  if (!quote) {
    return false;
  }
  return "route" in quote;
}
export function getClassicQuoteFromResponse(quote) {
  return isClassicQuote(quote?.quote) ? quote.quote : void 0;
}
export function getBridgeQuoteFromResponse(quote) {
  return quote?.routing === Routing.BRIDGE ? quote.quote : void 0;
}
export function validateTrade({
  trade,
  currencyIn,
  currencyOut,
  exactAmount,
  exactCurrencyField
}) {
  if (!trade || !currencyIn || !currencyOut || !exactAmount) {
    return null;
  }
  const inputsMatch = areAddressesEqual(currencyIn.wrapped.address, trade?.inputAmount.currency.wrapped.address);
  const outputsMatch = areAddressesEqual(currencyOut.wrapped.address, trade.outputAmount.currency.wrapped.address);
  const tokenAddressesMatch = inputsMatch && outputsMatch;
  if (!tokenAddressesMatch) {
    logger.error(new Error(`Mismatched address in swap trade`), {
      tags: { file: "tradingApi/utils", function: "validateTrade" },
      extra: {
        formState: {
          currencyIdIn: currencyId(currencyIn),
          currencyIdOut: currencyId(currencyOut),
          exactAmount: exactAmount.toExact(),
          exactCurrencyField
        },
        tradeProperties: trade
      }
    });
    return null;
  }
  return trade;
}
export function useQuoteRoutingParams({
  selectedProtocols,
  tokenInChainId,
  tokenOutChainId,
  isUSDQuote,
  isV4HookPoolsEnabled = true
}) {
  const protocols = useProtocolsForChain(selectedProtocols ?? DEFAULT_PROTOCOL_OPTIONS, tokenInChainId);
  const v4HooksToggleFFEnabled = useFeatureFlag(FeatureFlags.SwapSettingsV4HooksToggle);
  return useMemo(() => {
    if (isUSDQuote) {
      return {
        protocols: [ProtocolItems.V2, ProtocolItems.V3, ProtocolItems.V4]
      };
    }
    if (tokenInChainId !== tokenOutChainId) {
      return { routingPreference: RoutingPreference.BEST_PRICE };
    }
    if (!v4HooksToggleFFEnabled) {
      return { protocols };
    } else {
      let finalProtocols = [...protocols];
      let hooksOptions;
      if (isV4HookPoolsEnabled) {
        if (!protocols.includes(ProtocolItems.V4)) {
          finalProtocols = [...protocols, ProtocolItems.V4];
          hooksOptions = HooksOptions.V4_HOOKS_ONLY;
        } else {
          hooksOptions = HooksOptions.V4_HOOKS_INCLUSIVE;
        }
      } else {
        hooksOptions = HooksOptions.V4_NO_HOOKS;
      }
      return { protocols: finalProtocols, hooksOptions };
    }
  }, [isUSDQuote, tokenInChainId, tokenOutChainId, protocols, isV4HookPoolsEnabled, v4HooksToggleFFEnabled]);
}
const DEFAULT_L2_SLIPPAGE_TOLERANCE_VALUE = 2.5;
export function useQuoteSlippageParams({
  customSlippageTolerance,
  tokenInChainId,
  tokenOutChainId,
  isUSDQuote
}) {
  const minAutoSlippageToleranceL2 = useDynamicConfigValue(
    DynamicConfigs.Swap,
    SwapConfigKey.MinAutoSlippageToleranceL2,
    DEFAULT_L2_SLIPPAGE_TOLERANCE_VALUE
  );
  return useMemo(() => {
    if (customSlippageTolerance) {
      return { slippageTolerance: customSlippageTolerance };
    }
    if (tokenInChainId !== tokenOutChainId || isUSDQuote) {
      return void 0;
    }
    if (isL2ChainId(tokenInChainId)) {
      return { slippageTolerance: minAutoSlippageToleranceL2 };
    }
    return { autoSlippage: AutoSlippage.DEFAULT };
  }, [customSlippageTolerance, isUSDQuote, minAutoSlippageToleranceL2, tokenInChainId, tokenOutChainId]);
}
